import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, CardContent,
  Button, CircularProgress, Chip, Avatar, Autocomplete, TextField,
  Collapse, Divider, Tooltip, IconButton, useMediaQuery, useTheme
} from "@mui/material";
import {
  LocationOn, Phone, ExpandMore, ExpandLess, Close, FilterList, Nature,
  AccountBalance, Store, ChevronRight, Inventory2, FiberManualRecord
} from "@mui/icons-material";
import { useNavigate } from 'react-router-dom';
import { useUser } from "../../Context/UserContext";
import { GetApi } from "../../Services/api/ApiCall";
import API_URLS from "../../Services/api/action";

// --- Fixed circle color for all centers ---
const CIRCLE_COLOR = '#1b5e20';

// --- Distance-based color ---
const getDistanceStyle = (km) => {
  const d = parseFloat(km) || 0;
  if (d <= 5) return { bg: '#1b5e20', color: '#ffffff' };
  if (d <= 25) return { bg: '#e65100', color: '#ffffff' };
  if (d <= 100) return { bg: '#f57f17', color: '#ffffff' };
  return { bg: '#c62828', color: '#ffffff' };
};

// --- Reusable input styles ---
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    fontSize: '14px',
    '& fieldset': { borderColor: '#c8e6c9' },
    '&:hover fieldset': { borderColor: '#66bb6a' },
    '&.Mui-focused fieldset': { borderColor: '#1b5e20', borderWidth: '2px' }
  }
};

const RequestSampling = () => {
  const { token, userId } = useUser();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // --- STATES ---
  const [productionCenters, setProductionCenters] = useState([]);
  const [centersLoading, setCentersLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedCards, setExpandedCards] = useState({});

  // Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedSpecies, setSelectedSpecies] = useState([]);

  // Master Data
  const [districts, setDistricts] = useState([]);
  const [speciesList, setSpeciesList] = useState([]);
  const [speciesLoading, setSpeciesLoading] = useState(false);

  // Filter panel toggle for mobile
  const [showFilters, setShowFilters] = useState(false);

  // --- EFFECTS ---

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        setSpeciesLoading(true);
        const [distRes, speciesRes] = await Promise.all([
          GetApi(`${API_URLS.GetDistricts}`, token),
          GetApi(`${API_URLS.GetSpecies}`, token),
        ]);
        const rawDistricts = Array.isArray(distRes) ? distRes : [];
        setDistricts(rawDistricts);
        setSpeciesList(Array.isArray(speciesRes) ? speciesRes : []);
      } catch (err) {
        console.error("Master data fetch error", err);
      } finally {
        setSpeciesLoading(false);
      }
    };
    fetchMasterData();
  }, [token]);

  useEffect(() => {
    const fetchProductionCenters = async () => {
      if (!token) return;
      try {
        setCentersLoading(true);
        setError("");
        const url = `${API_URLS.GetNearbyCenters || '/api/center/production-centers-nearby/'}?farmer_id=${userId}`;
        const res = await GetApi(url, token);
        const raw = res.results || res.data || res || [];
        const sorted = [...raw].sort((a, b) =>
          (parseFloat(a.distance_km) || 0) - (parseFloat(b.distance_km) || 0)
        );
        setProductionCenters(sorted);
      } catch (err) {
        console.error("Failed to fetch production centers", err);
        setError("Failed to load production centers. Please try again.");
      } finally {
        setCentersLoading(false);
      }
    };
    fetchProductionCenters();
  }, [token, userId]);

  // --- HANDLERS ---
  const handleCenterClick = (centerId) => navigate(`/request-species/${centerId}/${userId}`);
  const toggleCardExpand = (centerId) => {
    setExpandedCards(prev => ({ ...prev, [centerId]: !prev[centerId] }));
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDistrict(null);
    setSelectedSpecies([]);
  };

  // --- FILTER LOGIC ---
  const filteredCenters = productionCenters.filter((center) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      center.name_of_production_centre?.toLowerCase().includes(q) ||
      center.village_name?.toLowerCase().includes(q) ||
      center.district_name?.toLowerCase().includes(q) ||
      center.block_name?.toLowerCase().includes(q) ||
      center.complete_address?.toLowerCase().includes(q) ||
      center.production_center_code?.toLowerCase().includes(q);

    const matchesDistrict = !selectedDistrict || String(center.district_id) === String(selectedDistrict.id);

    const matchesSpecies = selectedSpecies.length === 0 ||
      (center.stock || []).some(s =>
        selectedSpecies.some(sel =>
          sel.name_tamil?.trim() === s.species_name_tamil?.trim() ||
          sel.s_name?.trim().toLowerCase() === s.species_name?.trim().toLowerCase()
        )
      );

    return matchesSearch && matchesDistrict && matchesSpecies;
  });

  const hasActiveFilters = searchQuery || selectedDistrict || selectedSpecies.length > 0;

  // ==================== MOBILE LIST ITEM ====================
  const renderMobileListItem = (center) => {
    const isExpanded = expandedCards[center.id];
    const stockItems = center.stock || [];
    const firstLetter = (center.name_of_production_centre || center.name || "P").charAt(0).toUpperCase();
    const isGovt = center.production_type === 'government';
    const distStyle = getDistanceStyle(center.distance_km);
    const address = center.complete_address ||
      [center.village_name, center.block_name, center.district_name].filter(Boolean).join(', ');

    return (
      <Paper
        key={center.id}
        elevation={0}
        sx={{
          borderRadius: 2.5,
          overflow: "hidden",
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          border: isExpanded ? '1.5px solid #66bb6a' : '1px solid #e8e8e8',
          mb: 2,
          transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
          ...(isExpanded && { boxShadow: '0 2px 10px rgba(27,94,32,0.12)' })
        }}
      >
        {/* Top green strip */}
        <Box sx={{
          height: '3px',
          background: 'linear-gradient(90deg, #1b5e20 0%, #66bb6a 100%)'
        }} />

        {/* Main content — NOT tappable for expand */}
        <Box sx={{ px: 2, py: 1.8 }}>
          {/* Row 1: Avatar | Name + Type + Distance */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Avatar
              sx={{
                width: 42, height: 42,
                bgcolor: CIRCLE_COLOR, fontSize: '18px', fontWeight: 800,
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(27,94,32,0.2)',
                border: '2.5px solid #ffffff'
              }}
            >
              {firstLetter}
            </Avatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                <Typography
                  variant="body1"
                  fontWeight={700}
                  noWrap
                  sx={{ color: '#212121', lineHeight: 1.3, fontSize: '14px', maxWidth: '55%' }}
                >
                  {center.name_of_production_centre || center.name || "Production Center"}
                </Typography>
                <Chip
                  icon={isGovt
                    ? <AccountBalance sx={{ fontSize: '10px !important', color: '#fff !important', ml: '3px !important' }} />
                    : <Store sx={{ fontSize: '10px !important', color: '#fff !important', ml: '3px !important' }} />
                  }
                  label={(center.production_type || 'N/A').toUpperCase()}
                  size="small"
                  sx={{
                    height: 18, fontSize: '7px', fontWeight: 800, letterSpacing: '0.6px',
                    bgcolor: isGovt ? '#1b5e20' : '#e65100',
                    color: '#ffffff', borderRadius: 1,
                    '& .MuiChip-icon': { color: '#ffffff' }
                  }}
                />
              </Box>

              <Typography variant="caption" sx={{
                color: '#9e9e9e', fontWeight: 600,
                letterSpacing: '0.5px', fontSize: '10px', display: 'block', mt: 0.2
              }}>
                {center.production_center_code}
              </Typography>
            </Box>

            {/* Distance chip — far right */}
            <Box sx={{ flexShrink: 0 }}>
              {center.distance_km != null && (
                <Chip
                  icon={<LocationOn sx={{ fontSize: '10px !important', color: `${distStyle.color} !important`, ml: '3px !important' }} />}
                  label={`${parseFloat(center.distance_km).toFixed(1)} km`}
                  size="small"
                  sx={{
                    height: 22, fontSize: '10px', fontWeight: 700,
                    bgcolor: distStyle.bg, color: distStyle.color, borderRadius: 1.5,
                    '& .MuiChip-icon': { color: distStyle.color }
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Address */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 1 }}>
            <LocationOn sx={{ fontSize: 13, color: '#81c784', flexShrink: 0, mt: 0.2 }} />
            <Typography variant="body2" color="text.secondary" sx={{
              fontSize: '11.5px', lineHeight: 1.4,
              overflow: 'hidden', textOverflow: 'ellipsis',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
            }}>
              {address}
            </Typography>
          </Box>

          {/* Contact */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.6 }}>
            <Phone sx={{ fontSize: 12, color: '#ffcc80', flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontSize: '11.5px', color: '#424242' }}>
              <Box component="span" fontWeight={600}>{center.contact_person || 'N/A'}</Box>
              {center.contact_person && center.mobile_number && (
                <Box component="span" sx={{ mx: 0.5, color: '#bdbdbd' }}>·</Box>
              )}
              <Box component="a" href={`tel:${center.mobile_number}`}
                onClick={(e) => e.stopPropagation()}
                sx={{ color: '#1b5e20', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                {center.mobile_number || ''}
              </Box>
            </Typography>
          </Box>

          {/* Quick species preview pills — always visible */}
          {stockItems.length > 0 && (
            <Box sx={{
              display: 'flex', flexWrap: 'wrap', gap: 0.5,
              mt: 1.2, alignItems: 'center'
            }}>
              {stockItems.slice(0, 3).map((stock, idx) => (
                <Chip
                  key={`${center.id}-pill-${idx}`}
                  label={stock.species_name?.trim()}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '10px',
                    fontWeight: 600,
                    bgcolor: '#f1f8e9',
                    color: '#2e7d32',
                    border: '1px solid #c8e6c9',
                    borderRadius: 1.5,
                    maxWidth: 120,
                    '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                  }}
                />
              ))}
              {stockItems.length > 3 && (
                <Chip
                  label={`+${stockItems.length - 3}`}
                  size="small"
                  sx={{
                    height: 22, fontSize: '10px', fontWeight: 700,
                    bgcolor: '#e8f5e9', color: '#1b5e20',
                    borderRadius: 1.5
                  }}
                />
              )}
            </Box>
          )}

          {stockItems.length === 0 && (
            <Box sx={{
              mt: 1.2, px: 1, py: 0.8, borderRadius: 1.5,
              bgcolor: '#fafafa', border: '1px dashed #e0e0e0'
            }}>
              <Typography variant="caption" sx={{ fontSize: '11px', color: '#9e9e9e' }}>
                No stock data available
              </Typography>
            </Box>
          )}
        </Box>

        {/* ========== ACTION BUTTONS BAR ========== */}
        <Box sx={{
          px: 2, pb: 2,
          display: 'flex', gap: 1, alignItems: 'stretch'
        }}>
          {/* Stock Details Button — toggleable */}
          {stockItems.length > 0 && (
            <Button
              variant="outlined"
              startIcon={
                isExpanded
                  ? <ExpandLess sx={{ fontSize: 16 }} />
                  : <Inventory2 sx={{ fontSize: 15 }} />
              }
              onClick={() => toggleCardExpand(center.id)}
              sx={{
                flex: 1,
                borderColor: isExpanded ? '#1b5e20' : '#c8e6c9',
                bgcolor: isExpanded ? '#e8f5e9' : '#ffffff',
                color: '#1b5e20',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '12px',
                borderRadius: 2,
                py: 0.8,
                '&:hover': {
                  borderColor: '#66bb6a',
                  bgcolor: '#e8f5e9'
                },
                '&:active': { transform: 'scale(0.98)' }
              }}
            >
              {isExpanded ? 'Hide Stock' : 'Stock Details'}
            </Button>
          )}

          {/* Request Saplings Button — always visible */}
          <Button
            variant="contained"
            endIcon={<ChevronRight sx={{ fontSize: 16 }} />}
            onClick={() => handleCenterClick(center.id)}
            sx={{
              flex: 1,
              bgcolor: '#1b5e20',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '12px',
              borderRadius: 2,
              py: 0.8,
              boxShadow: '0 2px 8px rgba(27,94,32,0.2)',
              '&:hover': {
                bgcolor: '#0d3b13',
                boxShadow: '0 3px 10px rgba(27,94,32,0.3)'
              },
              '&:active': { transform: 'scale(0.98)' }
            }}
          >
            Request Saplings
          </Button>
        </Box>

        {/* ========== EXPANDED STOCK SECTION ========== */}
        <Collapse in={isExpanded} timeout={300} unmountOnExit>
          <Divider sx={{ mx: 2, borderColor: '#e8e8e8' }} />

          <Box sx={{
            px: 2, pt: 1.5, pb: 2,
            bgcolor: '#fafff9',
            borderTop: 'none'
          }}>
            {/* Section header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              mb: 1.2, px: 0.8
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <FiberManualRecord sx={{ fontSize: 8, color: '#66bb6a' }} />
                <Typography variant="caption" fontWeight={800} sx={{ color: '#2e7d32', fontSize: '10px', letterSpacing: '0.8px' }}>
                  FULL STOCK LIST
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ fontSize: '10px', color: '#9e9e9e', fontWeight: 600 }}>
                {stockItems.length} species
              </Typography>
            </Box>

            {/* Stock rows */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {stockItems.map((stock, idx) => (
                <Box
                  key={`${center.id}-stock-${idx}`}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 1,
                    py: 0.7,
                    borderRadius: 1.5,
                    bgcolor: '#ffffff',
                    border: '1px solid #e8f5e9',
                    borderLeft: '3px solid #66bb6a',
                    gap: 1
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" noWrap fontWeight={600} sx={{ fontSize: '12px', color: '#333' }}>
                      {stock.species_name?.trim()}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9e9e9e', fontSize: '10px' }}>
                      {stock.species_name_tamil?.trim()}
                      {stock.sapling_age && ` · ${stock.sapling_age}`}
                    </Typography>
                  </Box>
                  <Chip
                    label={stock.saplings_available}
                    size="small"
                    sx={{
                      height: 22, fontSize: '10px', fontWeight: 800,
                      bgcolor: '#1b5e20', color: '#ffffff',
                      minWidth: 38, justifyContent: 'center',
                      flexShrink: 0, borderRadius: 1.5
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Collapse>
      </Paper>
    );
  };

  // ==================== DESKTOP CARD ====================
  const renderDesktopCard = (center) => {
    const isExpanded = expandedCards[center.id];
    const stockItems = center.stock || [];
    const hasMore = stockItems.length > 2;
    const firstLetter = (center.name_of_production_centre || center.name || "P").charAt(0).toUpperCase();
    const isGovt = center.production_type === 'government';
    const distStyle = getDistanceStyle(center.distance_km);

    return (
      <Paper
        key={center.id}
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          cursor: 'pointer',
          transition: 'box-shadow 0.3s ease, transform 0.3s ease',
          border: '1px solid #ebebeb',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            transform: 'translateY(-4px)'
          }
        }}
      >
        <Box sx={{ height: '4px', background: 'linear-gradient(90deg, #1b5e20 0%, #66bb6a 60%, #a5d6a7 100%)' }} />

        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>

          <Box sx={{ p: 2.5, pb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.8, alignItems: 'flex-start' }}>
              <Avatar
                sx={{
                  width: 52, height: 52, bgcolor: CIRCLE_COLOR,
                  fontSize: '24px', fontWeight: 800, flexShrink: 0,
                  boxShadow: '0 3px 12px rgba(27,94,32,0.25)',
                  border: '3px solid #ffffff', color: '#ffffff'
                }}
              >
                {firstLetter}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap
                  sx={{ color: '#212121', lineHeight: 1.3, fontSize: '16px' }}>
                  {center.name_of_production_centre || center.name || "Production Center"}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9e9e9e', fontWeight: 600, letterSpacing: '0.8px', fontSize: '11px' }}>
                  {center.production_center_code}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
                <Chip
                  icon={isGovt
                    ? <AccountBalance sx={{ fontSize: '13px !important', color: '#fff !important', ml: '4px !important' }} />
                    : <Store sx={{ fontSize: '13px !important', color: '#fff !important', ml: '4px !important' }} />
                  }
                  label={(center.production_type || 'N/A').toUpperCase()}
                  size="small"
                  sx={{ height: 22, fontSize: '8px', fontWeight: 800, letterSpacing: '0.8px', bgcolor: isGovt ? '#1b5e20' : '#e65100', color: '#ffffff', borderRadius: 1.5, '& .MuiChip-icon': { color: '#ffffff' } }}
                />
                {center.distance_km != null && (
                  <Chip
                    icon={<LocationOn sx={{ fontSize: '12px !important', color: `${distStyle.color} !important`, ml: '4px !important' }} />}
                    label={`${parseFloat(center.distance_km).toFixed(1)} km`}
                    size="small"
                    sx={{ height: 22, fontSize: '10px', fontWeight: 700, bgcolor: distStyle.bg, color: distStyle.color, borderRadius: 1.5, '& .MuiChip-icon': { color: distStyle.color } }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mx: 2.5, borderColor: '#f0f0f0' }} />

          <Box sx={{ px: 2.5, py: 2 }}>
            <Tooltip title={center.complete_address || ''} arrow placement="top">
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 26, height: 26, borderRadius: '7px', bgcolor: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2 }}>
                  <LocationOn sx={{ fontSize: 14, color: '#2e7d32' }} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{
                  lineHeight: 1.5, fontSize: '13px',
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
                }}>
                  {center.complete_address || [center.village_name, center.block_name, center.district_name].filter(Boolean).join(', ')}
                </Typography>
              </Box>
            </Tooltip>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box sx={{ width: 26, height: 26, borderRadius: '7px', bgcolor: '#fff3e0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Phone sx={{ fontSize: 13, color: '#e65100' }} />
                </Box>
                <Typography variant="body2" sx={{ color: '#424242', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Box component="span" fontWeight={700}>{center.contact_person || 'N/A'}</Box>
                  {center.contact_person && center.mobile_number && (
                    <Box component="span" sx={{ mx: 0.8, color: '#bdbdbd' }}>·</Box>
                  )}
                  <Box component="a" href={`tel:${center.mobile_number}`} onClick={(e) => e.stopPropagation()}
                    sx={{ color: '#1b5e20', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    {center.mobile_number || ''}
                  </Box>
                </Typography>
              </Box>

              <Box
                onClick={(e) => { e.stopPropagation(); handleCenterClick(center.id); }}
                sx={{
                  flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.4,
                  cursor: 'pointer', p: 1, borderRadius: 3, bgcolor: '#e8f5e9',
                  transition: 'all 0.2s ease',
                  '&:hover': { bgcolor: '#1b5e20', '& .shop-text': { color: '#ffffff' }, '& .shop-arrow': { color: '#ffffff' } }
                }}
              >
                <Typography className="shop-text" sx={{ fontSize: '14px', fontWeight: 700, color: '#1b5e20', transition: 'color 0.2s ease', whiteSpace: 'nowrap' }}>
                  Shop Here
                </Typography>
                <Typography className="shop-arrow" sx={{ fontSize: '14px', color: '#1b5e20', transition: 'color 0.2s ease', lineHeight: 1 }}>
                  →
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mx: 2.5, borderColor: '#f0f0f0' }} />

          <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.2, py: 0.6, px: 1, borderRadius: 1.5, bgcolor: stockItems.length > 0 ? '#f1f8e9' : '#fafafa' }}>
              <Nature sx={{ fontSize: 13, color: stockItems.length > 0 ? '#2e7d32' : '#9e9e9e' }} />
              <Typography variant="caption" fontWeight={700} sx={{ color: stockItems.length > 0 ? '#2e7d32' : '#9e9e9e', fontSize: '10px', letterSpacing: '0.5px' }}>
                {stockItems.length > 0 ? `${stockItems.length} SPECIES AVAILABLE` : 'NO STOCK DATA'}
              </Typography>
            </Box>

            {stockItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '12px', px: 1.5, py: 1.2, textAlign: 'center', bgcolor: '#fafafa', borderRadius: 2, border: '1px dashed #e0e0e0' }}>
                No stock data available
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                {stockItems.slice(0, 2).map((stock, idx) => (
                  <Box key={`first-${center.id}-${idx}`} sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    px: 1.2, py: 0.8, borderRadius: 1.5, bgcolor: '#ffffff',
                    border: '1px solid #f0f0f0', borderLeft: '3px solid #66bb6a', gap: 1
                  }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap fontWeight={600} sx={{ fontSize: '12px', color: '#333' }}>{stock.species_name?.trim()}</Typography>
                      <Typography variant="caption" sx={{ color: '#9e9e9e', fontSize: '10px' }}>
                        {stock.species_name_tamil?.trim()}{stock.sapling_age && ` · ${stock.sapling_age}`}
                      </Typography>
                    </Box>
                    <Chip label={stock.saplings_available} size="small" sx={{
                      height: 22, fontSize: '10px', fontWeight: 800, bgcolor: '#1b5e20',
                      color: '#ffffff', minWidth: 42, justifyContent: 'center', flexShrink: 0, borderRadius: 1.5
                    }} />
                  </Box>
                ))}

                <Collapse in={isExpanded} timeout={300} unmountOnExit={false}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                    {stockItems.slice(2).map((stock, idx) => (
                      <Box key={`more-${center.id}-${idx}`} sx={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        px: 1.2, py: 0.8, borderRadius: 1.5, bgcolor: '#ffffff',
                        border: '1px solid #f0f0f0', borderLeft: '3px solid #a5d6a7', gap: 1
                      }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" noWrap fontWeight={600} sx={{ fontSize: '12px', color: '#333' }}>{stock.species_name?.trim()}</Typography>
                          <Typography variant="caption" sx={{ color: '#9e9e9e', fontSize: '10px' }}>
                            {stock.species_name_tamil?.trim()}{stock.sapling_age && ` · ${stock.sapling_age}`}
                          </Typography>
                        </Box>
                        <Chip label={stock.saplings_available} size="small" sx={{
                          height: 22, fontSize: '10px', fontWeight: 800, bgcolor: '#2e7d32',
                          color: '#ffffff', minWidth: 42, justifyContent: 'center', flexShrink: 0, borderRadius: 1.5
                        }} />
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}

            {hasMore && (
              <Button
                size="small"
                endIcon={isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                onClick={(e) => { e.stopPropagation(); toggleCardExpand(center.id); }}
                sx={{
                  mt: 0.8, mb: 0.5, color: '#e65100', textTransform: 'none',
                  fontWeight: 700, fontSize: '11px', p: '2px 4px', justifyContent: 'flex-start',
                  '&:hover': { bgcolor: '#fff3e0', borderRadius: 1 }
                }}
              >
                {isExpanded ? 'Show Less' : `+${stockItems.length - 2} More Species`}
              </Button>
            )}
          </Box>
        </CardContent>
      </Paper>
    );
  };

  // ==================== MAIN RENDER ====================
  return (
    <Box sx={{
      p: isMobile ? 1 : { sm: 2, md: 3, lg: 4 },
      bgcolor: "#f0f4f0",
      minHeight: "100vh"
    }}>
      <Box maxWidth="1600px" mx="auto">

        {/* ==================== HEADER ==================== */}
        <Box
          mb={isMobile ? 1.5 : 3}
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{
              color: '#1b5e20', display: 'flex', alignItems: 'center', gap: 1,
              letterSpacing: '-0.3px', fontSize: isMobile ? '1.1rem' : { sm: '1.5rem' }
            }}>
              <Nature sx={{ fontSize: isMobile ? 22 : 30 }} />
              Production Centers
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.3} sx={{ fontSize: isMobile ? '11px' : '13px' }}>
              Browse nearby centers sorted by distance · Request saplings
            </Typography>
          </Box>

          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterList sx={{ fontSize: 16 }} />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  flex: 1, borderColor: '#c8e6c9', color: '#1b5e20',
                  textTransform: 'none', fontWeight: 600, fontSize: '12px', borderRadius: 2,
                  bgcolor: showFilters ? '#e8f5e9' : '#ffffff',
                  justifyContent: 'flex-start',
                  '&:hover': { borderColor: '#66bb6a', bgcolor: '#e8f5e9' }
                }}
              >
                Filters
                {hasActiveFilters && (
                  <Chip label={filteredCenters.length} size="small" sx={{
                    ml: 1, height: 18, minWidth: 18, fontSize: '10px',
                    bgcolor: '#1b5e20', color: '#fff', fontWeight: 700, borderRadius: '9px'
                  }} />
                )}
              </Button>
              {productionCenters.length > 0 && (
                <Chip
                  icon={<LocationOn sx={{ fontSize: '13px !important' }} />}
                  label={`${productionCenters.length}`}
                  sx={{ bgcolor: '#1b5e20', color: '#fff', fontWeight: 600, fontSize: '11px', height: 30, borderRadius: 2, '& .MuiChip-icon': { color: '#a5d6a7' } }}
                />
              )}
            </Box>
          )}

          {!isMobile && productionCenters.length > 0 && (
            <Chip
              icon={<LocationOn sx={{ fontSize: '14px !important' }} />}
              label={`${productionCenters.length} center${productionCenters.length !== 1 ? 's' : ''} found`}
              sx={{ bgcolor: '#1b5e20', color: '#fff', fontWeight: 600, fontSize: '12px', height: 30, borderRadius: 2, '& .MuiChip-icon': { color: '#a5d6a7' } }}
            />
          )}
        </Box>

        {/* ==================== FILTERS ==================== */}
        {isMobile && (
          <Collapse in={showFilters} timeout={300}>
            <Paper elevation={0} sx={{
              p: 2, mb: 2, borderRadius: 2.5, border: '1px solid #c8e6c9', bgcolor: '#ffffff',
              position: 'relative', overflow: 'hidden',
              '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px', background: 'linear-gradient(180deg, #1b5e20 0%, #66bb6a 100%)' }
            }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <TextField size="small" placeholder="Search center..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={inputSx}
                  InputProps={{
                    startAdornment: <LocationOn sx={{ mr: 1, color: '#81c784', fontSize: 16 }} />,
                    endAdornment: searchQuery && (
                      <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ p: 0.3, color: '#9e9e9e' }}><Close sx={{ fontSize: 14 }} /></IconButton>
                    )
                  }}
                />
                <Autocomplete size="small" options={districts} getOptionLabel={(o) => o.District_Name || o.name || ''} value={selectedDistrict} onChange={(e, v) => setSelectedDistrict(v)} filterSelectedOptions sx={inputSx}
                  isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="District..." size="small"
                      InputProps={{ ...params.InputProps, startAdornment: (<><LocationOn sx={{ mr: 1, color: '#81c784', fontSize: 16 }} />{params.InputProps.startAdornment}</>) }}
                    />
                  )}
                  renderOption={(props, option) => { const { key, ...r } = props; return (<li key={key} {...r}><Typography variant="body2" fontWeight={500} sx={{ fontSize: '13px', color: '#1b5e20' }}>{option.District_Name || option.name}</Typography></li>); }}
                  renderTags={() => null}
                />
                <Autocomplete multiple size="small" options={speciesList} getOptionLabel={(o) => `${o.name} / ${o.name_tamil}`} value={selectedSpecies} onChange={(e, v) => setSelectedSpecies(v)} filterSelectedOptions loading={speciesLoading} sx={inputSx}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Species..." size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (<><Nature sx={{ mr: 1, color: '#81c784', fontSize: 16 }} />{params.InputProps.startAdornment}</>),
                        endAdornment: (<>{speciesLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}{params.InputProps.endAdornment}</>)
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) => value.map((option, index) => { const { key, ...rest } = getTagProps({ index }); return (<Chip key={key} label={option.name} size="small" onDelete={rest.onDelete} sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '10px', height: 22, border: '1px solid #ffcc80', '& .MuiChip-deleteIcon': { color: '#ff9800', '&:hover': { color: '#e65100' } } }} {...rest} />); })}
                  renderOption={(props, option) => { const { key, ...r } = props; return (<li key={key} {...r}><Box sx={{ py: 0.5 }}><Typography variant="body2" fontWeight={600} sx={{ fontSize: '13px', lineHeight: 1.3, color: '#1b5e20' }}>{option.name}</Typography><Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>{option.name_tamil}</Typography></Box></li>); }}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                />
                {hasActiveFilters && (
                  <Button size="small" startIcon={<Close fontSize="small" />} onClick={clearFilters} sx={{ color: '#c62828', textTransform: 'none', fontWeight: 700, fontSize: '12px', alignSelf: 'flex-end' }}>
                    Clear All
                  </Button>
                )}
              </Box>
            </Paper>
          </Collapse>
        )}

        {!isMobile && (
          <Paper elevation={0} sx={{
            p: 2.5, mb: 3, borderRadius: 3, border: '1px solid #c8e6c9', bgcolor: '#ffffff',
            position: 'relative', overflow: 'hidden',
            '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', background: 'linear-gradient(180deg, #1b5e20 0%, #66bb6a 100%)', borderRadius: '4px 0 0 4px' }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, ml: 0.5 }}>
              <FilterList sx={{ fontSize: 17, color: '#2e7d32' }} />
              <Typography variant="subtitle2" fontWeight={700} color="#2e7d32" fontSize="13px" sx={{ letterSpacing: '0.3px' }}>FILTERS</Typography>
              {hasActiveFilters && (
                <Chip label={`${filteredCenters.length} result${filteredCenters.length !== 1 ? 's' : ''}`} size="small" sx={{ ml: 'auto', bgcolor: '#e8f5e9', color: '#1b5e20', fontWeight: 700, fontSize: '11px', height: 24, borderRadius: 1.5 }} />
              )}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '260px 220px 1fr', gap: 2, alignItems: 'flex-start' }}>
              <TextField size="small" placeholder="Search by production center..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={inputSx}
                InputProps={{
                  startAdornment: <LocationOn sx={{ mr: 1, color: '#81c784', fontSize: 18 }} />,
                  endAdornment: searchQuery && (<IconButton size="small" onClick={() => setSearchQuery("")} sx={{ p: 0.3, color: '#9e9e9e' }}><Close sx={{ fontSize: 16 }} /></IconButton>)
                }}
              />
              <Autocomplete size="small" options={districts} getOptionLabel={(o) => o.District_Name || o.name || ''} value={selectedDistrict} onChange={(e, v) => setSelectedDistrict(v)} filterSelectedOptions sx={inputSx}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Filter by district..." size="small"
                    InputProps={{ ...params.InputProps, startAdornment: (<><LocationOn sx={{ mr: 1, color: '#81c784', fontSize: 18 }} />{params.InputProps.startAdornment}</>) }}
                  />
                )}
                renderOption={(props, option) => { const { key, ...r } = props; return (<li key={key} {...r}><Typography variant="body2" fontWeight={500} sx={{ fontSize: '13px', color: '#1b5e20' }}>{option.District_Name || option.name}</Typography></li>); }}
                renderTags={() => null}
              />
              <Autocomplete multiple size="small" options={speciesList} getOptionLabel={(o) => `${o.name} / ${o.name_tamil}`} value={selectedSpecies} onChange={(e, v) => setSelectedSpecies(v)} filterSelectedOptions loading={speciesLoading} sx={inputSx}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Filter by species..." size="small"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (<><Nature sx={{ mr: 1, color: '#81c784', fontSize: 18 }} />{params.InputProps.startAdornment}</>),
                      endAdornment: (<>{speciesLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}{params.InputProps.endAdornment}</>)
                    }}
                  />
                )}
                renderTags={(value, getTagProps) => value.map((option, index) => { const { key, ...rest } = getTagProps({ index }); return (<Chip key={key} label={option.name} size="small" onDelete={rest.onDelete} sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '11px', height: 24, border: '1px solid #ffcc80', '& .MuiChip-deleteIcon': { color: '#ff9800', '&:hover': { color: '#e65100' } } }} {...rest} />); })}
                renderOption={(props, option) => { const { key, ...r } = props; return (<li key={key} {...r}><Box sx={{ py: 0.5 }}><Typography variant="body2" fontWeight={600} sx={{ fontSize: '13px', lineHeight: 1.3, color: '#1b5e20' }}>{option.name}</Typography><Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>{option.name_tamil}</Typography></Box></li>); }}
                isOptionEqualToValue={(o, v) => o.id === v.id}
              />
              {hasActiveFilters && (
                <Box sx={{ gridColumn: '1/-1', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button size="small" startIcon={<Close fontSize="small" />} onClick={clearFilters} sx={{ color: '#c62828', textTransform: 'none', fontWeight: 700, fontSize: '13px' }}>Clear All</Button>
                </Box>
              )}
            </Box>
          </Paper>
        )}

        {/* ==================== LOADING ==================== */}
        {centersLoading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={8}>
            <CircularProgress size={50} sx={{ color: '#1b5e20', mb: 2 }} />
            <Typography color="text.secondary" fontWeight={500}>Finding production centers near you...</Typography>
          </Box>
        ) : error ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={8} bgcolor="#fff" borderRadius={3}>
            <Typography color="error" mb={2}>{error}</Typography>
            <Button variant="outlined" onClick={() => window.location.reload()}>Retry</Button>
          </Box>
        ) : filteredCenters.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" p={8} bgcolor="#fff" borderRadius={3}>
            <Typography variant="h6" color="text.secondary" mb={1}>No Centers Found</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {productionCenters.length === 0 ? "No production centers available at the moment." : "Try adjusting your filters to see more results."}
            </Typography>
          </Box>
        ) : (
          /* ==================== LIST / GRID ==================== */
          isMobile ? (
            <Box>
              {hasActiveFilters && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, mb: 1, bgcolor: '#e8f5e9', borderRadius: 2 }}>
                  <Typography variant="caption" sx={{ fontSize: '11px', color: '#2e7d32', fontWeight: 700 }}>
                    {filteredCenters.length} result{filteredCenters.length !== 1 ? 's' : ''}
                  </Typography>
                  <Button size="small" onClick={clearFilters} sx={{ ml: 'auto', color: '#c62828', textTransform: 'none', fontWeight: 700, fontSize: '11px', p: '0 4px', minWidth: 'auto' }}>Clear</Button>
                </Box>
              )}
              {filteredCenters.map((center) => renderMobileListItem(center))}
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 2.5 }}>
              {filteredCenters.map((center) => renderDesktopCard(center))}
            </Box>
          )
        )}

        <Box sx={{ height: { xs: 24, sm: 40 } }} />
      </Box>
    </Box>
  );
};

export default RequestSampling;